import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import './styles/globals.css';

// Landing
const LandingPage       = lazy(() => import('./pages/landing/LandingPage'));
const ServicesPage      = lazy(() => import('./pages/landing/ServicesPage'));
const ServiceDetailPage = lazy(() => import('./pages/landing/ServiceDetailPage'));
const CartPage          = lazy(() => import('./pages/landing/CartPage'));
const CheckoutPage      = lazy(() => import('./pages/landing/CheckoutPage'));
const CashfreeReturn    = lazy(() => import('./pages/landing/CashfreeReturn'));

// Auth
const LoginPage    = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));

// Admin
const AdminLayout      = lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard   = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminLeads       = lazy(() => import('./pages/admin/AdminLeads'));
const AdminSalesTeam   = lazy(() => import('./pages/admin/AdminSalesTeam'));
const AdminOrders      = lazy(() => import('./pages/admin/AdminOrders'));
const AdminServices    = lazy(() => import('./pages/admin/AdminServices'));
const AdminCustomers   = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminProjects    = lazy(() => import('./pages/admin/AdminProjects'));
const AdminCommissions = lazy(() => import('./pages/admin/AdminCommissions'));
const AdminTickets     = lazy(() => import('./pages/admin/AdminTickets'));
const AdminSupport     = lazy(() => import('./pages/admin/AdminSupport'));
const AdminBilling     = lazy(() => import('./pages/admin/AdminBilling'));
const AdminPerformance = lazy(() => import('./pages/admin/AdminPerformance'));
const AdminReports     = lazy(() => import('./pages/admin/AdminReports'));
const AdminKYC           = lazy(() => import('./pages/admin/AdminKYC'));
const AdminTrials    = lazy(() => import('./pages/admin/AdminTrials'));
const AdminPayLater  = lazy(() => import('./pages/admin/AdminPayLater'));

// Salesman
const SalesmanLayout      = lazy(() => import('./components/salesman/SalesmanLayout'));
const SalesmanDashboard   = lazy(() => import('./pages/salesman/SalesmanDashboard'));
const SalesmanLeads       = lazy(() => import('./pages/salesman/SalesmanLeads'));
const SalesmanDeals       = lazy(() => import('./pages/salesman/SalesmanDeals'));
const SalesmanCommissions = lazy(() => import('./pages/salesman/SalesmanCommissions'));
const SalesmanBilling     = lazy(() => import('./pages/salesman/SalesmanBilling'));
const SalesmanSupport     = lazy(() => import('./pages/salesman/SalesmanSupport'));
const SalesmanPerformance = lazy(() => import('./pages/salesman/SalesmanPerformance'));
const SalesmanServices    = lazy(() => import('./pages/salesman/SalesmanServices'));
const SalesmanCart        = lazy(() => import('./pages/salesman/SalesmanCart'));
const SalesmanReports     = lazy(() => import('./pages/salesman/SalesmanReports'));
const SalesmanReferral    = lazy(() => import('./pages/salesman/SalesmanReferral'));
const SalesmanKYC         = lazy(() => import('./pages/salesman/SalesmanKYC'));

// Customer
const CustomerLayout   = lazy(() => import('./components/customer/CustomerLayout'));
const CustomerDashboard= lazy(() => import('./pages/customer/CustomerDashboard'));
const CustomerProjects = lazy(() => import('./pages/customer/CustomerProjects'));
const CustomerInvoices = lazy(() => import('./pages/customer/CustomerInvoices'));
const CustomerTickets  = lazy(() => import('./pages/customer/CustomerTickets'));
const CustomerSupport  = lazy(() => import('./pages/customer/CustomerSupport'));
const CustomerServices = lazy(() => import('./pages/customer/CustomerServices'));
const CustomerCart     = lazy(() => import('./pages/customer/CustomerCart'));
const CustomerReports  = lazy(() => import('./pages/customer/CustomerReports'));

const LoadingScreen = () => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:'#f8fafc' }}>
    <div style={{ textAlign:'center' }}>
      <div className="spinner" style={{ margin:'0 auto 16px' }} />
      <p style={{ color:'#64748b', fontSize:14 }}>Loading...</p>
    </div>
  </div>
);

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    const dest = user.role === 'admin' ? '/admin' : user.role === 'salesman' ? '/salesman' : '/customer';
    return <Navigate to={dest} replace />;
  }
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public */}
        <Route path="/"              element={<LandingPage />} />
        <Route path="/services"      element={<ServicesPage />} />
        <Route path="/services/:slug"element={<ServiceDetailPage />} />
        <Route path="/cart"          element={<CartPage />} />

        <Route path="/login"         element={user ? <Navigate to={`/${user.role}`}/> : <LoginPage />} />
        <Route path="/register"      element={user ? <Navigate to={`/${user.role}`}/> : <RegisterPage />} />

        {/* Admin */}
        <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminLayout /></ProtectedRoute>}>
          <Route index             element={<AdminDashboard />} />
          <Route path="leads"      element={<AdminLeads />} />
          <Route path="sales-team" element={<AdminSalesTeam />} />
          <Route path="orders"     element={<AdminOrders />} />
          <Route path="services"   element={<AdminServices />} />
          <Route path="customers"  element={<AdminCustomers />} />
          <Route path="projects"   element={<AdminProjects />} />
          <Route path="commissions"element={<AdminCommissions />} />
          <Route path="support"    element={<AdminSupport />} />
          <Route path="billing"    element={<AdminBilling />} />
          <Route path="performance"element={<AdminPerformance />} />
          <Route path="reports"     element={<AdminReports />} />
          <Route path="kyc"          element={<AdminKYC/>}/>
              <Route path="trials"     element={<AdminTrials />} />
          <Route path="pay-later"  element={<AdminPayLater />} />
        </Route>

        {/* Salesman */}
        <Route path="/salesman" element={<ProtectedRoute roles={['salesman']}><SalesmanLayout /></ProtectedRoute>}>
          <Route index               element={<SalesmanDashboard />} />
          <Route path="leads"        element={<SalesmanLeads />} />
          <Route path="deals"        element={<SalesmanDeals />} />
          <Route path="commissions"  element={<SalesmanCommissions />} />
          <Route path="billing"      element={<SalesmanBilling />} />
          <Route path="support"      element={<SalesmanSupport />} />
          <Route path="performance"  element={<SalesmanPerformance />} />
          <Route path="services"     element={<SalesmanServices />} />
          <Route path="cart"         element={<SalesmanCart />} />
          <Route path="reports"      element={<SalesmanReports />} />
          <Route path="referral"     element={<SalesmanReferral />} />
          <Route path="kyc"          element={<SalesmanKYC/>}/>
          <Route path="checkout"     element={<CheckoutPage />} />
          <Route path="checkout/cashfree-return" element={<CashfreeReturn />} />
        </Route>

        {/* Customer */}
        <Route path="/customer" element={<ProtectedRoute roles={['customer']}><CustomerLayout /></ProtectedRoute>}>
          <Route index             element={<CustomerDashboard />} />
          <Route path="projects"   element={<CustomerProjects />} />
          <Route path="invoices"   element={<CustomerInvoices />} />
          <Route path="support"    element={<CustomerSupport />} />
          <Route path="tickets"    element={<CustomerTickets />} />
          <Route path="services"   element={<CustomerServices />} />
          <Route path="cart"       element={<CustomerCart />} />
          <Route path="reports"    element={<CustomerReports />} />
          <Route path="checkout"   element={<CheckoutPage />} />
          <Route path="checkout/cashfree-return" element={<CashfreeReturn />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" toastOptions={{ duration: 3000, style: { fontFamily:'Plus Jakarta Sans', fontSize:14 } }} />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
